import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { ShowreelItem } from '../../content/showreel';

type ShowreelTileProps = {
  item: ShowreelItem;
  isActivePlayer: boolean;
  isUnmuted: boolean;
  dialogOpen: boolean;
  onPlayingChange: (id: string, playing: boolean) => void;
  onUnmuteChange: (id: string, unmuted: boolean) => void;
  onOpen: (item: ShowreelItem) => void;
};

const POSTER_TIME = 0.05;

function MuteIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
        <path d="M11 5 6 9H3v6h3l5 4V5z" />
        <path d="m23 9-6 6M17 9l6 6" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <path d="M11 5 6 9H3v6h3l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

export function ShowreelTile({
  item,
  isActivePlayer,
  isUnmuted,
  dialogOpen,
  onPlayingChange,
  onUnmuteChange,
  onOpen,
}: ShowreelTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [srcReady, setSrcReady] = useState(false);
  const [hovering, setHovering] = useState(false);
  const coarseRef = useRef(false);

  useEffect(() => {
    coarseRef.current =
      typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  }, []);

  // Lazy-attach src when near viewport so first frame can paint.
  useEffect(() => {
    const card = cardRef.current;
    if (!card || srcReady) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setSrcReady(true);
        observer.disconnect();
      },
      { rootMargin: '200px 0px', threshold: 0 },
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [srcReady]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !srcReady) return;

    const paintFrame = () => {
      try {
        if (video.readyState >= 1 && video.currentTime < POSTER_TIME) {
          video.currentTime = POSTER_TIME;
        }
      } catch {
        // Ignore seek errors before ready.
      }
    };

    video.addEventListener('loadedmetadata', paintFrame);
    if (video.readyState >= 1) paintFrame();
    return () => video.removeEventListener('loadedmetadata', paintFrame);
  }, [srcReady, item.src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !isUnmuted;
  }, [isUnmuted]);

  useEffect(() => {
    if (!dialogOpen) return;
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setHovering(false);
    onPlayingChange(item.id, false);
  }, [dialogOpen, item.id, onPlayingChange]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isActivePlayer) return;
    video.pause();
    if (!hovering) {
      try {
        video.currentTime = POSTER_TIME;
      } catch {
        // ignore
      }
    }
  }, [isActivePlayer, hovering]);

  const play = async () => {
    if (dialogOpen) return;
    const video = videoRef.current;
    if (!video) return;
    if (!srcReady) setSrcReady(true);
    video.muted = !isUnmuted;
    onPlayingChange(item.id, true);
    try {
      await video.play();
    } catch {
      // Autoplay can fail; ignore.
    }
  };

  const stop = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    try {
      video.currentTime = POSTER_TIME;
    } catch {
      // ignore
    }
    onPlayingChange(item.id, false);
    if (isUnmuted) onUnmuteChange(item.id, false);
  };

  const onPointerEnter = () => {
    if (coarseRef.current || dialogOpen) return;
    setHovering(true);
    void play();
  };

  const onPointerLeave = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (coarseRef.current || dialogOpen) return;
    const next = e.relatedTarget;
    if (next instanceof Node && cardRef.current?.contains(next)) return;
    setHovering(false);
    stop();
  };

  const onCardClick = () => {
    onOpen(item);
  };

  const onMuteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!srcReady) setSrcReady(true);
    const nextUnmuted = !isUnmuted;
    onUnmuteChange(item.id, nextUnmuted);
    const video = videoRef.current;
    if (video) {
      video.muted = !nextUnmuted;
      if (nextUnmuted && video.paused && !dialogOpen) void play();
    }
  };

  const showMute = hovering || isActivePlayer;

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      aria-label={`Open ${item.title}`}
      className="group relative aspect-video w-full overflow-hidden bg-[#080808] cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
    >
      <video
        ref={videoRef}
        src={srcReady ? item.src : undefined}
        muted
        loop
        playsInline
        preload="metadata"
        poster={item.poster}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        aria-hidden="true"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/25 pointer-events-none" />

      <button
        type="button"
        onClick={onMuteClick}
        aria-label={isUnmuted ? `Mute ${item.title}` : `Unmute ${item.title}`}
        className={[
          'absolute top-2 right-2 z-[2] size-8 rounded-full grid place-items-center',
          'text-white/85 hover:text-white border border-white/25 hover:border-white/50',
          'bg-black/50 hover:bg-black/70 transition-opacity duration-150',
          showMute
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-visible:opacity-100 group-focus-visible:pointer-events-auto',
        ].join(' ')}
      >
        <MuteIcon muted={!isUnmuted} />
      </button>

      <div className="absolute left-2.5 right-12 bottom-2.5 pointer-events-none">
        {item.tag ? (
          <p className="font-mono text-[8px] tracking-[0.18em] uppercase text-white/50 m-0 mb-0.5">
            {item.tag}
          </p>
        ) : null}
        <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-white/85 m-0 truncate">
          {item.title}
        </p>
      </div>
    </div>
  );
}
